import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { FaCalendar, FaClock, FaArrowRight } from 'react-icons/fa'
import PageHero from '../../components/PageHero/PageHero'

interface BlogPost {
  id: string
  slug: string
  title: string
  excerpt: string
  category: string
  date: string
  readTime: string
  image: string
}

const blogPosts: BlogPost[] = [
  {
    id: '1',
    slug: 'bitcoin-mining-tax-strategy-2025',
    title: 'Bitcoin Mining Tax Strategy: Maximize 2025 Deductions',
    excerpt: 'Learn how to optimize your bitcoin mining operations for maximum tax efficiency in 2025. Discover key deductions, depreciation strategies, and compliance requirements.',
    category: 'Tax & Compliance',
    date: '2025-01-15',
    readTime: '8 min read',
    image: 'bitcoin-mining'
  },
  {
    id: '2',
    slug: 'genius-act-stablecoin-compliance',
    title: 'GENIUS Act Stablecoin Law Explained: The Compliance Countdown Has Begun',
    excerpt: 'Understanding the new regulatory framework for stablecoins and what issuers need to know to ensure compliance with the GENIUS Act.',
    category: 'Regulatory Updates',
    date: '2025-01-10',
    readTime: '12 min read',
    image: 'genius-act'
  },
  {
    id: '3',
    slug: 'real-time-por-vs-monthly-attestations',
    title: 'Real-Time Proof of Reserves vs. Monthly Attestations: Which is Right for You?',
    excerpt: 'Compare traditional monthly attestations with cutting-edge real-time proof of reserves. Learn the benefits, costs, and implementation considerations.',
    category: 'Technical Guide',
    date: '2025-01-05',
    readTime: '10 min read',
    image: 'por-comparison'
  },
  {
    id: '4',
    slug: 'rwa-tokenization-audit-requirements',
    title: 'RWA Tokenization: Essential Audit Requirements for 2025',
    excerpt: 'As real-world asset tokenization accelerates, understanding audit and attestation requirements is critical for issuers and investors alike.',
    category: 'RWA & Tokenization',
    date: '2024-12-28',
    readTime: '9 min read',
    image: 'rwa-tokenization'
  },
  {
    id: '5',
    slug: 'merkle-tree-verification-explained',
    title: 'Merkle Tree Verification: A Deep Dive into Cryptographic Proof',
    excerpt: 'Understand how Merkle tree verification provides cryptographic proof of reserves while protecting user privacy and enabling individual verification.',
    category: 'Technical Guide',
    date: '2024-12-20',
    readTime: '15 min read',
    image: 'merkle-tree'
  },
  {
    id: '6',
    slug: 'stablecoin-reserve-transparency-best-practices',
    title: 'Stablecoin Reserve Transparency: Industry Best Practices',
    excerpt: 'Explore the current state of stablecoin reserve reporting and the best practices leading issuers are implementing to build trust.',
    category: 'Industry Analysis',
    date: '2024-12-15',
    readTime: '11 min read',
    image: 'stablecoin-transparency'
  },
  {
    id: '7',
    slug: 'on-chain-por-smart-contract-integration',
    title: 'On-Chain Proof of Reserves: Smart Contract Integration Guide',
    excerpt: 'Learn how to integrate on-chain proof of reserves data into your smart contracts for automated controls, insurance products, and circuit breakers.',
    category: 'Technical Guide',
    date: '2024-12-10',
    readTime: '13 min read',
    image: 'on-chain-integration'
  },
  {
    id: '8',
    slug: 'crypto-accounting-standards-2025',
    title: 'Crypto Accounting Standards: What Changed in 2025',
    excerpt: 'A comprehensive overview of the latest accounting standards updates affecting cryptocurrency businesses and digital asset holders.',
    category: 'Accounting & CFO',
    date: '2024-12-05',
    readTime: '10 min read',
    image: 'accounting-standards'
  },
  {
    id: '9',
    slug: 'exchange-custody-audit-checklist',
    title: 'Exchange & Custody Provider Audit Checklist: Complete Guide',
    excerpt: 'Essential checklist for cryptocurrency exchanges and custody providers preparing for their first proof of reserves audit.',
    category: 'Audit Preparation',
    date: '2024-11-28',
    readTime: '14 min read',
    image: 'audit-checklist'
  }
]

const categories = ['All', 'Technical Guide', 'Regulatory Updates', 'Tax & Compliance', 'RWA & Tokenization', 'Industry Analysis', 'Accounting & CFO', 'Audit Preparation']

const Blog = () => {
  return (
    <>
      <Helmet>
        <title>Insights & Resources - Proof of Reserves Auditor</title>
        <meta name="description" content="Latest insights, technical guides, and industry analysis about proof of reserves, blockchain security, and crypto compliance." />
      </Helmet>

      <div className="min-h-screen">
        <PageHero
          title={
            <>
              Insights & <span className="text-accent">Resources</span>
            </>
          }
          description="Expert analysis, technical guides, and industry updates on proof of reserves, digital asset auditing, and blockchain transparency"
          className="py-20"
        />

        {/* Category Filter */}
        <section className="bg-white py-8 border-b border-gray-200">
          <div className="container-custom">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category, index) => (
                <button
                  key={index}
                  className={`px-6 py-2 rounded-full font-semibold transition-colors ${
                    index === 0
                      ? 'bg-accent text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Post */}
        <section className="py-16 bg-gray-50">
          <div className="container-custom">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-primary mb-2">Featured Article</h2>
              <div className="h-1 w-24 bg-accent"></div>
            </div>
            
            <Link to={`/blog/${blogPosts[0].slug}`} className="block">
              <div className="grid md:grid-cols-2 gap-8 bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
                <div className="h-80 md:h-auto bg-gradient-to-br from-accent to-primary flex items-center justify-center">
                  <div className="text-white text-6xl font-bold opacity-20">PoR</div>
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <span className="inline-block bg-accent text-white px-4 py-1 rounded-full text-sm font-semibold mb-4 w-fit">
                    {blogPosts[0].category}
                  </span>
                  <h3 className="text-3xl font-bold text-primary mb-4 hover:text-accent transition-colors">
                    {blogPosts[0].title}
                  </h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    {blogPosts[0].excerpt}
                  </p>
                  <div className="flex items-center gap-6 text-gray-500 text-sm mb-6">
                    <div className="flex items-center gap-2">
                      <FaCalendar className="text-accent" />
                      <span>{new Date(blogPosts[0].date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaClock className="text-accent" />
                      <span>{blogPosts[0].readTime}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-accent font-semibold group">
                    <span>Read Full Article</span>
                    <FaArrowRight className="group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* All Posts Grid */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-primary mb-2">Latest Articles</h2>
              <div className="h-1 w-24 bg-accent"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.slice(1).map((post) => (
                <Link 
                  key={post.id} 
                  to={`/blog/${post.slug}`}
                  className="group"
                >
                  <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow h-full flex flex-col">
                    <div className="h-48 bg-gradient-to-br from-accent to-primary flex items-center justify-center relative overflow-hidden">
                      <div className="text-white text-4xl font-bold opacity-20 group-hover:scale-110 transition-transform">
                        {post.category.split(' ')[0]}
                      </div>
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <span className="inline-block bg-gray-100 text-accent px-3 py-1 rounded-full text-xs font-semibold mb-3 w-fit">
                        {post.category}
                      </span>
                      <h3 className="text-xl font-bold text-primary mb-3 group-hover:text-accent transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 mb-4 flex-1 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-gray-500 text-sm pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <FaCalendar className="text-accent text-xs" />
                          <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FaClock className="text-accent text-xs" />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-16 bg-gradient-to-br from-primary to-primary-dark text-white">
          <div className="container-custom text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Stay Updated</h2>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Subscribe to our newsletter for the latest insights on proof of reserves, compliance updates, and industry trends.
            </p>
            <div className="max-w-md mx-auto flex gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <button className="bg-accent hover:bg-accent-dark text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Blog